import React from 'react';
import styled, { keyframes } from 'styled-components';
// Helmet is no longer needed here as meta is hardcoded in index.html
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube, faCrosshairs, faDoorOpen } from '@fortawesome/free-solid-svg-icons';
import { PageMeta } from 'components/Layout/Page'


const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PageWrapper = styled.div`
  color: #e0e0e0;
  font-family: 'Orbitron', sans-serif;
  background-color: #0a0a0f;
`;
const Section = styled.section`
  padding: 100px 40px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  animation: ${fadeIn} 1s ease-out forwards;
  opacity: 0;
`;
const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 900;
  margin-bottom: 20px;
  color: #00ff9d;
  text-transform: uppercase;
  letter-spacing: 2px;
`;
const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0b0;
  max-width: 800px;
  margin: 0 auto 60px auto;
  line-height: 1.7;
`;
const HeroSection = styled.header`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  padding: 20px;
  background: url('https://hub.thenex.world/images/background.png') no-repeat center center/cover;
  &::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(ellipse at center, rgba(10, 10, 15, 0.7) 0%, rgba(10, 10, 15, 0.95) 100%);
  }
`;
const HeroContent = styled.div`
  position: relative; z-index: 1; animation: ${fadeIn} 1s ease-out; max-width: 900px;
`;
const MainTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 5.5rem); font-weight: 900; color: #fff;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.3); margin-bottom: 1.5rem; line-height: 1.1;
  text-transform: uppercase;
`;
const CtaButton = styled.a`
  display: inline-block; padding: 15px 40px; font-size: 1.2rem; font-weight: 700;
  color: #0a0a0f; background: #00ff9d; border: none; border-radius: 5px; text-decoration: none;
  cursor: pointer; transition: all 0.3s ease;
  box-shadow: 0 0 25px rgba(0, 255, 157, 0.5); margin: 10px;
  &:hover { transform: scale(1.05); box-shadow: 0 0 40px rgba(0, 255, 157, 0.8); }
`;
const SecondaryCtaButton = styled(CtaButton)`
  background: transparent;
  color: #00ff9d;
  border: 2px solid #00ff9d;
  box-shadow: none;
  &:hover { background: rgba(0, 255, 157, 0.1); box-shadow: 0 0 25px rgba(0, 255, 157, 0.4); }
`;
const TrustBar = styled.div`
  background-color: #050508;
  padding: 20px 40px;
  text-align: center;
  color: #a0a0b0;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  span {
    font-weight: 700;
    color: #fff;
    margin: 0 15px;
  }
`;
const PillarsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 40px;
`;
const PillarCard = styled.div`
  background: #12121a; padding: 40px; border-radius: 10px;
  border: 1px solid #2a2a3a; transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover { transform: translateY(-10px); box-shadow: 0 10px 30px rgba(0, 255, 157, 0.1); }
`;
const PillarIcon = styled(FontAwesomeIcon)`
  font-size: 3rem; color: #00ff9d; margin-bottom: 20px;
`;
const PillarTitle = styled.h3`
  font-size: 1.75rem; margin-bottom: 15px; color: #fff;
`;
const PillarDescription = styled.p`
  color: #a0a0b0; line-height: 1.6;
`;
const NftShowcaseSection = styled(Section)`
  background: #050508;
`;
const NftShowcaseGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  text-align: left;
  flex-direction: column;
  @media (min-width: 992px) {
    flex-direction: row;
  }
`;
const NftImageWrapper = styled.div`
  flex: 1;
  max-width: 500px;
  img {
    width: 100%;
    border-radius: 15px;
    box-shadow: 0 0 40px rgba(0, 255, 157, 0.3);
  }
`;
const NftContent = styled.div`
  flex: 1.2;
`;
const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 20px;
  li {
    padding-left: 30px;
    position: relative;
    margin-bottom: 10px;
    font-size: 1.1rem;
    color: #a0a0b0;
    &:before {
      content: '✔';
      color: #00ff9d;
      position: absolute;
      left: 0;
      font-weight: bold;
    }
  }
`;
const VideoWrapper = styled.div`
  position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;
  border-radius: 10px; border: 2px solid #00ff9d; box-shadow: 0 0 30px rgba(0, 255, 157, 0.3);
  max-width: 960px; margin: 0 auto;
`;
const ResponsiveIframe = styled.iframe`
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
`;

// --- The Main Component ---
const HomePage = () => {
  return (
    <>
      <PageMeta />
      <PageWrapper>
        <main>
          {/* HERO SECTION: Optimized for ICP and Keywords */}
          <HeroSection>
            <HeroContent>
              <MainTitle as="h1">THE SCI-FI EXTRACTION RPG</MainTitle>
              <SectionSubtitle style={{fontSize: '1.3rem'}}>
                Fusing the tactical gunplay of Halo with the unforgiving, high-stakes extraction of Tarkov and Dark & Darker. This is the persistent blockchain MMO where your loot is your legacy.
              </SectionSubtitle>
              <CtaButton href="/game">PLAY FOR FREE</CtaButton>
              <SecondaryCtaButton href="#pillars">LEARN MORE</SecondaryCtaButton>
            </HeroContent>
          </HeroSection>

          {/* TRUST BAR: Directly calls out to the broader sci-fi/fantasy ICP */}
          <TrustBar>
            <span>Powered by WebGL & WebXR technologies</span> | <span>For Fans Of Ready Player One & The Matrix</span> | <span>Secured by Blockchain</span>
          </TrustBar>

          {/* PILLARS SECTION: Directly contrasts with traditional MMOs */}
          <Section id="pillars">
            <SectionTitle as="h2">THE MMO, REFORGED</SectionTitle>
            <SectionSubtitle>Tired of grinding for gear in games like World of Warcraft or RuneScape that you never truly own? We are too. The NEX is founded on principles that put power back in your hands.</SectionSubtitle>
            <PillarsGrid>
              <PillarCard>
                <PillarIcon icon={faCube} />
                <PillarTitle as="h3">True Asset Ownership</PillarTitle>
                <PillarDescription>Unlike traditional MMOs, every legendary weapon and rare armor piece is a blockchain NFT. You don't just use it—you own it. Sell it on the marketplace, trade it, or build your digital armory.</PillarDescription>
              </PillarCard>
              <PillarCard>
                <PillarIcon icon={faCrosshairs} />
                <PillarTitle as="h3">High-Stakes Extraction</PillarTitle>
                <PillarDescription>Inspired by Ghosts of Tabor and Tarkov, every run is a high-stakes gamble. Navigate treacherous PvPvE zones, secure valuable NFT loot, and extract safely—or lose it all.</PillarDescription>
              </PillarCard>
              <PillarCard>
                <PillarIcon icon={faDoorOpen} />
                <PillarTitle as="h3">Radical Accessibility</PillarTitle>
                <PillarDescription>The NEX is free-to-play, period. No purchase required to jump in and compete. Our 'Play & Earn' model ensures everyone has a genuine opportunity to earn valuable assets through skill and dedication.</PillarDescription>
              </PillarCard>
            </PillarsGrid>
          </Section>

          {/* NFT SHOWCASE: Connects the NFT to the core gameplay loop */}
          <NftShowcaseSection id="glitch-nft">
            <NftShowcaseGrid>
              <NftImageWrapper>
                <img src="/images/glitch/glitchNft.gif" alt="GLITCH Boss Monster NFT" />
              </NftImageWrapper>
              <NftContent>
                <SectionTitle as="h2" style={{textAlign: 'left', margin: 0}}>Command the Anomaly</SectionTitle>
                <PillarDescription style={{fontSize: '1.1rem', marginTop: '20px'}}>
                  The GLITCH NFT is your master key to The NEX ecosystem. Owning one elevates your status, accelerates your progress, and unlocks exclusive holder-only benefits.
                </PillarDescription>
                <BenefitList>
                  <li>Gain entry to the elite, holder-only player faction.</li>
                  <li>Receive monthly airdrops of rare items and currency.</li>
                  <li>Activate a permanent XP boost across all activities.</li>
                </BenefitList>
                <CtaButton href="/glitch" style={{marginTop: '30px'}}>MINT YOUR GLITCH</CtaButton>
              </NftContent>
            </NftShowcaseGrid>
          </NftShowcaseSection>

          {/* TRAILER SECTION: Standard and effective */}
          <Section id="trailer">
            <SectionTitle as="h2">See The NEX in Action</SectionTitle>
            <VideoWrapper>
              <ResponsiveIframe
                src="https://www.youtube.com/embed/BCMdLG46MCI?autoplay=1&start=0&rel=0"
                title="The NEX Gameplay Trailer"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoWrapper>
          </Section>
        </main>
      </PageWrapper>
    </>
  );
};

export default HomePage;